import { languageOf } from '../contract/defaults';
import { REGIONAL_DEFAULTS } from '../contract/regional';

/**
 * Wording the package owns, for the places CLDR offers none.
 *
 * Everything else this package renders is derived: a unit label, a date order, a relative phrase
 * all come from ICU, which knows more about each locale than we could maintain. A phrase reaches
 * this file only when ICU has no concept answering to it, and each one here is a small liability —
 * a string three languages have to agree on, kept correct by hand.
 *
 * Not an i18next namespace, for the same reason the gap-unit label catalogue is not: i18next is
 * not federation-shared, so a phrase resolved through one app's instance renders as a raw key
 * inside a remote.
 */

/** The languages the platform ships. A phrase has to answer in all of them to be added below. */
type PhraseLanguage = 'en' | 'fr' | 'es';

type Phrase = Readonly<Record<PhraseLanguage, string>>;

/**
 * `Just Now` is the one phrase the ladder needs and ICU cannot supply.
 *
 * `Intl.RelativeTimeFormat` answers `now` for a zero elapsed time, which is the same idea in a
 * different register — lowercase, and reading as an adverb rather than a label. The screens this
 * serves have said `Just Now` since before the package existed, and these three strings are the
 * ones they say it with, lifted from the app's own catalogue rather than retranslated.
 */
const JUST_NOW: Phrase = {
  en: 'Just Now',
  fr: "À l'instant",
  es: 'Justo Ahora',
};

export const PHRASES = { justNow: JUST_NOW } as const satisfies Readonly<
  Record<string, Phrase>
>;

export type PhraseKey = keyof typeof PHRASES;

/**
 * A phrase in the reader's language.
 *
 * Keyed on the language rather than the full locale, because these are sentences rather than
 * regional conventions: Canadian and metropolitan French say `À l'instant` alike. A region that
 * genuinely disagreed would want a per-region override table of the kind the gap-unit labels
 * carry, and there is none here until one does.
 *
 * @param locale - The reader's resolved locale.
 * @param key - Which phrase.
 * @returns The phrase, defaulting to {@link REGIONAL_DEFAULTS.language} for a language with no row.
 */
export const phraseIn = (locale: string, key: PhraseKey): string => {
  const language = languageOf(locale) ?? REGIONAL_DEFAULTS.language;
  const phrase = PHRASES[key];

  return (
    phrase[language as PhraseLanguage] ??
    phrase[REGIONAL_DEFAULTS.language as PhraseLanguage]
  );
};
